import { useTranslation } from "react-i18next";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, generateBreadcrumbSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export default function PrivacyPolicyPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith("pt");

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Política de Privacidade | RevOps AI" : "Privacy Policy | RevOps AI"}
        description={isPt ? "Política de privacidade do RevOps AI. Saiba como coletamos, usamos e protegemos seus dados." : "RevOps AI privacy policy. Learn how we collect, use and protect your data."}
        path="/privacy"
        
      />
      <SchemaMarkup schema={[generateBreadcrumbSchema([
        { name: 'Home', url: 'https://revops.digitais.ai/' },
        { name: isPt ? 'Privacidade' : 'Privacy', url: 'https://revops.digitais.ai/privacy' },
      ])]} />
      <MarketingNav />
      <main className="py-16 md:py-24">
        <div className="container max-w-3xl mx-auto">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Privacidade' : 'Privacy' }]} />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1F3C] mb-2">
            {isPt ? "Política de Privacidade" : "Privacy Policy"}
          </h1>
          <p className="text-sm text-[#1A1F3C]/40 mb-10">
            {isPt ? "Última atualização: 25 de março de 2026" : "Last updated: March 25, 2026"}
          </p>

          <div className="prose prose-lg max-w-none text-[#1A1F3C]/70 [&>h2]:text-[#1A1F3C] [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4 [&>h3]:text-[#1A1F3C] [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-2">
            {isPt ? (
              <>
                <h2>1. Introdução</h2>
                <p>
                  A DigitAIs ("nós", "nosso") opera a plataforma de RevOps AI disponível em revops.digitais.ai. Esta
                  política descreve como coletamos, usamos, armazenamos e protegemos seus dados pessoais e dados do CRM
                  da sua organização, em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018) e
                  demais legislações aplicáveis.
                </p>

                <h2>2. Dados que Coletamos</h2>
                <h3>2.1 Dados da Conta</h3>
                <p>Nome completo, email corporativo, cargo, telefone, organização, e preferências de configuração.</p>
                <h3>2.2 Dados do CRM</h3>
                <p>
                  Quando você conecta seu CRM (HubSpot, RD Station, ou outro), sincronizamos dados de deals, contatos,
                  empresas, atividades, notas e campos customizados mapeados. Esses dados são utilizados exclusivamente
                  para gerar análises, diagnósticos, forecasts e recomendações dentro da plataforma.
                </p>
                <h3>2.3 Dados de Uso</h3>
                <p>
                  Informações sobre como você interage com a plataforma, incluindo páginas visitadas, funcionalidades
                  utilizadas e configurações aplicadas.
                </p>
                <h3>2.4 Dados de Faturamento</h3>
                <p>
                  Informações de pagamento são processadas diretamente pelo Stripe. Não armazenamos dados de cartão de
                  crédito em nossos servidores.
                </p>

                <h2>3. Como Usamos seus Dados</h2>
                <p>Utilizamos os dados coletados para:</p>
                <ul>
                  <li>Fornecer análises de receita, forecasts matemáticos e recomendações de ação</li>
                  <li>Gerar diagnósticos de funil, pipeline e performance de vendas</li>
                  <li>
                    Treinar e melhorar nossos modelos de inteligência artificial (usando apenas dados agregados e
                    anonimizados)
                  </li>
                  <li>Enviar comunicações transacionais (confirmações, alertas, relatórios)</li>
                  <li>Gerenciar sua assinatura e faturamento</li>
                  <li>Garantir a segurança e integridade da plataforma</li>
                </ul>

                <h2>4. Compartilhamento de Dados</h2>
                <p>
                  <strong>Não vendemos seus dados.</strong> Compartilhamos dados apenas nas seguintes situações:
                </p>
                <ul>
                  <li>
                    <strong>Provedores de infraestrutura:</strong> Utilizamos serviços de cloud computing (Supabase,
                    Cloudflare) para hospedar e processar dados. Esses provedores são contratados sob acordos de
                    processamento de dados (DPA).
                  </li>
                  <li>
                    <strong>Processamento de pagamentos:</strong> O Stripe processa transações financeiras e tem acesso
                    apenas aos dados necessários para isso.
                  </li>
                  <li>
                    <strong>Integrações CRM:</strong> Os dados fluem entre seu CRM e nossa plataforma conforme sua
                    autorização explícita. Não acessamos dados além do escopo autorizado.
                  </li>
                  <li>
                    <strong>Obrigação legal:</strong> Quando exigido por lei, ordem judicial ou autoridade reguladora
                    competente.
                  </li>
                </ul>
                <p>
                  <strong>
                    Seus dados de CRM nunca são compartilhados com outras organizações, parceiros comerciais ou usados
                    para fins publicitários.
                  </strong>
                </p>

                <h2>5. Segurança dos Dados</h2>
                <ul>
                  <li>Criptografia em trânsito (TLS 1.3) e em repouso (AES-256)</li>
                  <li>Infraestrutura compatível com SOC 2</li>
                  <li>Controle de acesso baseado em funções (RBAC) com permissões granulares</li>
                  <li>Logs de auditoria para todas as ações sensíveis</li>
                  <li>Isolamento de dados por organização (multi-tenant seguro)</li>
                  <li>Backups automatizados com retenção configurável</li>
                </ul>

                <h2>6. Retenção de Dados</h2>
                <p>
                  Mantemos os dados do CRM sincronizados enquanto sua conta estiver ativa e a integração habilitada. Ao
                  desconectar o CRM ou cancelar a assinatura, os dados sincronizados são retidos por 30 dias para
                  permitir reconexão, após os quais são permanentemente deletados.
                </p>
                <p>Dados da conta são retidos por 90 dias após o encerramento, conforme obrigações fiscais e legais.</p>

                <h2>7. Seus Direitos (LGPD)</h2>
                <p>Você tem direito a:</p>
                <ul>
                  <li>Confirmação da existência de tratamento de dados</li>
                  <li>Acesso aos dados pessoais tratados</li>
                  <li>Correção de dados incompletos ou desatualizados</li>
                  <li>Anonimização, bloqueio ou eliminação de dados desnecessários</li>
                  <li>Portabilidade dos dados</li>
                  <li>Informação sobre compartilhamento com terceiros</li>
                  <li>Revogação do consentimento</li>
                </ul>
                <p>
                  Para exercer seus direitos, entre em contato pelo email: <strong>privacidade@digitais.ai</strong>
                </p>

                <h2>8. Cookies</h2>
                <p>
                  Utilizamos cookies essenciais para funcionamento da plataforma (autenticação, preferências de idioma).
                  Não utilizamos cookies de rastreamento publicitário.
                </p>

                <h2>9. Alterações nesta Política</h2>
                <p>
                  Podemos atualizar esta política periodicamente. Notificaremos sobre mudanças significativas por email
                  ou dentro da plataforma.
                </p>

                <h2>10. Contato</h2>
                <p>
                  Para dúvidas sobre esta política ou tratamento de dados:
                  <br />
                  <strong>Email:</strong> privacidade@digitais.ai
                  <br />
                  <strong>Encarregado de Dados (DPO):</strong> dpo@digitais.ai
                </p>
              </>
            ) : (
              <>
                <h2>1. Introduction</h2>
                <p>
                  DigitAIs ("we", "our") operates the RevOps AI platform available at revops.digitais.ai. This policy
                  describes how we collect, use, store, and protect your personal data and your organization's CRM data,
                  in compliance with applicable data protection laws including LGPD (Brazilian General Data Protection
                  Law) and GDPR.
                </p>

                <h2>2. Data We Collect</h2>
                <h3>2.1 Account Data</h3>
                <p>Full name, work email, job title, phone, organization, and configuration preferences.</p>
                <h3>2.2 CRM Data</h3>
                <p>
                  When you connect your CRM (HubSpot, RD Station, or other), we synchronize deals, contacts, companies,
                  activities, notes, and mapped custom fields. This data is used exclusively to generate analyses,
                  diagnostics, forecasts, and recommendations within the platform.
                </p>
                <h3>2.3 Usage Data</h3>
                <p>
                  Information about how you interact with the platform, including pages visited, features used, and
                  settings applied.
                </p>
                <h3>2.4 Billing Data</h3>
                <p>
                  Payment information is processed directly by Stripe. We do not store credit card data on our servers.
                </p>

                <h2>3. How We Use Your Data</h2>
                <p>We use collected data to:</p>
                <ul>
                  <li>Provide revenue analyses, mathematical forecasts, and action recommendations</li>
                  <li>Generate funnel, pipeline, and sales performance diagnostics</li>
                  <li>Train and improve our AI models (using only aggregated and anonymized data)</li>
                  <li>Send transactional communications (confirmations, alerts, reports)</li>
                  <li>Manage your subscription and billing</li>
                  <li>Ensure platform security and integrity</li>
                </ul>

                <h2>4. Data Sharing</h2>
                <p>
                  <strong>We do not sell your data.</strong> We share data only in the following situations:
                </p>
                <ul>
                  <li>
                    <strong>Infrastructure providers:</strong> We use cloud computing services (Supabase, Cloudflare) to
                    host and process data, contracted under data processing agreements (DPA).
                  </li>
                  <li>
                    <strong>Payment processing:</strong> Stripe processes financial transactions and only accesses data
                    necessary for that purpose.
                  </li>
                  <li>
                    <strong>CRM integrations:</strong> Data flows between your CRM and our platform per your explicit
                    authorization. We do not access data beyond the authorized scope.
                  </li>
                  <li>
                    <strong>Legal obligation:</strong> When required by law, court order, or competent regulatory
                    authority.
                  </li>
                </ul>
                <p>
                  <strong>
                    Your CRM data is never shared with other organizations, business partners, or used for advertising
                    purposes.
                  </strong>
                </p>

                <h2>5. Data Security</h2>
                <ul>
                  <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
                  <li>SOC 2 compliant infrastructure</li>
                  <li>Role-based access control (RBAC) with granular permissions</li>
                  <li>Audit logs for all sensitive actions</li>
                  <li>Data isolation per organization (secure multi-tenant)</li>
                  <li>Automated backups with configurable retention</li>
                </ul>

                <h2>6. Data Retention</h2>
                <p>
                  We retain synchronized CRM data as long as your account is active and the integration is enabled. Upon
                  CRM disconnection or subscription cancellation, synced data is retained for 30 days to allow
                  reconnection, after which it is permanently deleted.
                </p>
                <p>Account data is retained for 90 days after closure, per tax and legal obligations.</p>

                <h2>7. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                  <li>Confirmation of data processing</li>
                  <li>Access to processed personal data</li>
                  <li>Correction of incomplete or outdated data</li>
                  <li>Anonymization, blocking, or deletion of unnecessary data</li>
                  <li>Data portability</li>
                  <li>Information about third-party sharing</li>
                  <li>Consent revocation</li>
                </ul>
                <p>
                  To exercise your rights, contact us at: <strong>privacy@digitais.ai</strong>
                </p>

                <h2>8. Cookies</h2>
                <p>
                  We use essential cookies for platform functionality (authentication, language preferences). We do not
                  use advertising tracking cookies.
                </p>

                <h2>9. Changes to This Policy</h2>
                <p>
                  We may update this policy periodically. We will notify you of significant changes via email or within
                  the platform.
                </p>

                <h2>10. Contact</h2>
                <p>
                  For questions about this policy or data processing:
                  <br />
                  <strong>Email:</strong> privacy@digitais.ai
                  <br />
                  <strong>Data Protection Officer (DPO):</strong> dpo@digitais.ai
                </p>
              </>
            )}
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
